import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Check, RefreshCw, FlagOff } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { sortByCategoryOrder } from '../../../lib/categoryOrder';
import { Modal } from '../../ui/Modal';
import { pillPrimary, pillSecondary, pillGhost } from '../../ui/pills';
import { DishRow, LiveCheckins, contraintesDeTable } from './terrain/terrainTypes';
import { TerrainPlanDeTable } from './terrain/TerrainPlanDeTable';
import { TerrainPrep } from './terrain/TerrainPrep';

interface EvenementielEvent { id: string; type: 'PRIVÉ' | 'PROFESSIONNEL'; first_name?: string; last_name?: string; company_name?: string; start_time: string; }
const eventLabel = (e: EvenementielEvent) => e.type === 'PRIVÉ' ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : (e.company_name || '');
const fmtJour = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' }); } catch { return ''; }
};

type Onglet = 'plan' | 'allergies' | string;

const ONGLET = (actif: boolean, danger?: boolean) => `inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12.5px] font-semibold whitespace-nowrap shrink-0 transition-colors ${
    actif ? 'bg-content text-surface' : danger ? 'bg-state-danger-soft border border-state-danger/30 text-state-danger' : 'bg-surface border border-border text-content-muted hover:text-content'}`;

/**
 * Mode terrain (maquette 18d, refonte design lot 9) : le plan de table et les allergies sur téléphone
 * pendant le service, puis la vue cuisine par service. En-tête noir (le terrain reste hors ambiance,
 * comme le kiosque), onglets « Plan de table » / « Allergies N » / un par catégorie de plats. Les deux
 * premières vues ne font que lire ; les onglets de plats sont l'écran cuisine d'AVANT, inchangé
 * (Emeni, 16/09 : la forme « passe » de la maquette 24e était moins bonne — retirée). Rafraîchi toutes
 * les 15 secondes, comme avant.
 */
export default function ModeTerrainPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const [data, setData] = React.useState<LiveCheckins | null>(null);
    const [event, setEvent] = React.useState<EvenementielEvent | null>(null);
    const [validee, setValidee] = React.useState<boolean | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [onglet, setOnglet] = React.useState<Onglet>('plan');
    const [togglingKey, setTogglingKey] = React.useState<string | null>(null);

    // LE DOSSIER DERRIERE LE SERVICE. Le Mode terrain ne connait qu'un evenement ; c'est le serveur qui dit
    // s'il existe un dossier de privatisation a cloturer, et ou il en est. Silencieux en cas d'echec : un
    // etablissement sans le module « Suivi privatisation » recoit un refus, et ce n'est pas une erreur —
    // simplement, il n'y a rien a cloturer, donc pas de bouton.
    const [dossier, setDossier] = React.useState<{ dossier_id: string; label: string; stage: string; stage_label: string } | null>(null);
    const [confirmation, setConfirmation] = React.useState(false);
    const [cloture, setCloture] = React.useState(false);
    const [erreurCloture, setErreurCloture] = React.useState('');

    React.useEffect(() => {
        if (!eventId) return;
        moduleApi.getDossierByEvent(eventId).then((r: any) => setDossier(r?.dossier || null)).catch(() => setDossier(null));
        moduleApi.getEvenementielEvent(eventId).then((e: any) => setEvent(e)).catch(() => setEvent(null));
        moduleApi.getEvenementielCollecte(eventId).then((c: any) => setValidee(!!c?.production_locked_at)).catch(() => setValidee(null));
    }, [eventId]);

    const terminerLeService = async () => {
        if (!eventId) return;
        setCloture(true);
        setErreurCloture('');
        try {
            await moduleApi.finishServiceForEvent(eventId);
            setConfirmation(false);
            const r: any = await moduleApi.getDossierByEvent(eventId).catch(() => null);
            setDossier(r?.dossier || null);
        } catch (e: any) {
            setErreurCloture(e?.message || "Le dossier n'a pas pu etre cloture.");
        } finally {
            setCloture(false);
        }
    };

    const load = React.useCallback(async () => {
        if (!eventId) return;
        try {
            setData(await moduleApi.getEvenementielCollecteLiveCheckins(eventId));
        } catch {
            // silencieux — on retentera au prochain cycle de polling
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    React.useEffect(() => {
        load();
        const interval = setInterval(load, 15000);
        return () => clearInterval(interval);
    }, [load]);

    const categories = React.useMemo(() => {
        if (!data) return [];
        const set = new Set<string>();
        data.tables.forEach((t) => Object.keys(t.dish_counts_by_category).forEach((c) => set.add(c)));
        return sortByCategoryOrder(Array.from(set));
    }, [data]);

    const handleToggle = async (tableNumber: string, category: string, dish: DishRow) => {
        if (!eventId) return;
        const key = `${tableNumber}|${category}|${dish.dish_id}`;
        setTogglingKey(key);
        setData((prev) => prev ? {
            ...prev,
            tables: prev.tables.map((t) => t.table_number !== tableNumber ? t : {
                ...t,
                dish_counts_by_category: { ...t.dish_counts_by_category, [category]: t.dish_counts_by_category[category].map((d) => d.dish_id === dish.dish_id ? { ...d, prepared: !d.prepared } : d) },
            }),
        } : prev);
        try {
            await moduleApi.toggleEvenementielPrepStatus(eventId, { table_number: tableNumber, category, dish_id: dish.dish_id, count: dish.count });
        } catch {
            load();
        } finally {
            setTogglingKey(null);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    const tables = (data?.tables || []).filter((t) => t.guests.length > 0);
    const nbContraintes = tables.reduce((n, t) => n + contraintesDeTable(t).length, 0);
    const couverts = data?.total_checked_in ?? 0;
    const titre = event ? eventLabel(event) : dossier?.label || 'Service';

    return (
        <div className="space-y-3 max-w-[920px]">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                {/* Le Mode terrain est son propre module : le retour se fait sur SA liste, et non dans
                    Menus & Convives — un chef n'a pas forcement acces au module commercial. */}
                <button type="button" onClick={() => navigate('/mode-terrain')} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content">
                    <ArrowLeft size={14} /> Retour aux services
                </button>
                <button type="button" onClick={load} className={pillGhost(32)}><RefreshCw size={13} /> Actualiser</button>
            </div>

            <div className="rounded-[22px] bg-surface border border-border overflow-hidden">
                {/* En-tête noir : le terrain reste hors ambiance, lisible de loin (passation §7). */}
                <div className="px-4 pt-[13px] pb-3 bg-ink text-white">
                    <div className="flex items-center justify-between gap-2.5">
                        <span className="text-[10px] font-bold uppercase tracking-[.08em] text-white/55">Mode terrain</span>
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-white/55 whitespace-nowrap shrink-0">
                            <span className={`w-1.5 h-1.5 rounded-full ${validee ? 'bg-state-ok' : 'bg-state-warn'}`} />
                            {validee === null ? 'Service en cours' : validee ? 'Liste validée' : 'Liste non validée'}
                        </span>
                    </div>
                    <div className="mt-[7px] text-[17px] font-bold tracking-[-0.01em] truncate">{titre}</div>
                    <div className="mt-px text-[11.5px] text-white/70 tabular-nums">
                        {[fmtJour(event?.start_time), `${tables.length} table${tables.length > 1 ? 's' : ''}`, `${couverts} couvert${couverts > 1 ? 's' : ''}`].filter(Boolean).join(' · ')}
                        {dossier && <span> · {dossier.stage_label}</span>}
                    </div>
                </div>

                <div className="flex gap-[7px] px-4 py-3 border-b border-border-soft overflow-x-auto">
                    <button type="button" className={ONGLET(onglet === 'plan')} onClick={() => setOnglet('plan')}>Plan de table</button>
                    <button type="button" className={ONGLET(onglet === 'allergies', nbContraintes > 0)} onClick={() => setOnglet('allergies')}>Allergies<span className="tabular-nums">{nbContraintes}</span></button>
                    {categories.map((cat) => (
                        <button key={cat} type="button" className={ONGLET(onglet === cat)} onClick={() => setOnglet(cat)}>{cat}</button>
                    ))}
                </div>

                <div className="px-4 py-3.5">
                    {onglet === 'plan' ? <TerrainPlanDeTable tables={tables} />
                        : onglet === 'allergies' ? <TerrainPlanDeTable tables={tables} seulementAllergies />
                        : <TerrainPrep tables={data?.tables || []} category={onglet} togglingKey={togglingKey} onToggle={handleToggle} />}
                </div>

                {/* « Terminer le service » vit ICI et pas dans le suivi commercial : c'est le chef qui sait que
                    le service est fini (demande d'Emeni, 2026-08-26). La cloture n'envoie aucun email a personne. */}
                {dossier && (
                    <div className="px-4 py-3 border-t border-border-soft bg-surface-soft flex items-center justify-between gap-2.5 flex-wrap">
                        <span className="text-[12px] text-content-muted truncate">{dossier.label} — {dossier.stage_label}</span>
                        {dossier.stage !== 'cloturee' ? (
                            <button type="button" className={pillPrimary()} onClick={() => { setErreurCloture(''); setConfirmation(true); }}>
                                <FlagOff size={14} /> Terminer le service
                            </button>
                        ) : (
                            <span className="text-[12.5px] font-semibold text-state-ok inline-flex items-center gap-1.5"><Check size={14} /> Service terminé — dossier clôturé</span>
                        )}
                    </div>
                )}
            </div>

            {confirmation && dossier && (
                <Modal open onClose={() => setConfirmation(false)} size="sm" title="Terminer le service" subtitle="C'est la fin du parcours commercial de cette privatisation."
                    icon={<FlagOff size={16} />}
                    footer={
                        <>
                            <button type="button" className={pillSecondary()} onClick={() => setConfirmation(false)} disabled={cloture}>Annuler</button>
                            <button type="button" className={pillPrimary()} onClick={terminerLeService} disabled={cloture}>
                                {cloture ? <Loader2 size={13} className="animate-spin" /> : null} Terminer le service
                            </button>
                        </>
                    }>
                    <div className="space-y-3">
                        <p className="text-[13px] leading-relaxed text-content-muted text-pretty">
                            Le dossier <strong className="text-content">{dossier.label}</strong> passera de « {dossier.stage_label} » à <strong className="text-content">Clôturée</strong>.
                        </p>
                        <p className="text-[12px] leading-relaxed text-content-muted p-2.5 rounded-[10px] bg-surface-soft border border-border-soft text-pretty">
                            Aucun email n'est envoyé, ni au client ni aux convives. Les étapes qui n'auraient pas été renseignées à temps seront
                            notées comme passées dans l'historique du dossier, jamais comme accomplies.
                        </p>
                        {erreurCloture && <p className="text-[12px] text-state-danger font-semibold">{erreurCloture}</p>}
                    </div>
                </Modal>
            )}
        </div>
    );
}
